import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  AddReachProSupportCaseNoteRequest,
  Feature,
  ReachProSupportCaseClient,
} from 'src/WebApiController';

import { SUPPORT_CASE_NOTES_QUERY_KEY } from './useReachProSupportCaseNotes';
import { SUPPORT_CASES_QUERY_KEY } from './useReachProSupportCases';

export function useAddReachProSupportCaseNote() {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const queryClient = useQueryClient();
  const hasReachProSupportFeature = useUserHasFeature(Feature.ReachProSupport);

  return useMutation({
    mutationFn: async (request: AddReachProSupportCaseNoteRequest) => {
      if (!hasReachProSupportFeature) {
        throw new Error('Support Center feature is not enabled');
      }

      return tryInvokeApi(
        async () => {
          return await new ReachProSupportCaseClient(
            activeAccountWebClientConfig
          ).addSupportCaseNote(request);
        },
        (error) => {
          trackError('ReachProSupportCaseClient.addSupportCaseNote', error);
        }
      );
    },
    onSuccess: (_data, request) => {
      queryClient.invalidateQueries({
        queryKey: [SUPPORT_CASE_NOTES_QUERY_KEY, request.workItemId],
      });
      // Reorder the inbox and refresh the last-message preview after a reply
      queryClient.invalidateQueries({ queryKey: [SUPPORT_CASES_QUERY_KEY] });
    },
  });
}
